import { Check, X, Edit3, Clock, Mic, Camera, Keyboard, Share2 } from "lucide-react";
import type { InboxEntry, SuggestedActionItem } from "@/hooks/use-inbox";

/**
 * BW19 PR-D — single triage card.
 *
 * Renders the original capture, the AI's structured interpretation, and
 * the four single-keypress actions (Accept, Edit, Reject, Defer). Stays
 * dense by default per the Linear / Superhuman patterns the spec calls
 * out.
 */

const SOURCE_ICON: Record<string, typeof Mic> = {
  voice: Mic,
  photo: Camera,
  keyboard: Keyboard,
  share_sheet: Share2,
  browser_ext: Keyboard,
  email: Keyboard,
};

export function TriageCard({
  entry,
  active,
  onActivate,
  onAccept,
  onEdit,
  onReject,
  onDefer,
}: {
  entry: InboxEntry;
  active: boolean;
  onActivate: () => void;
  onAccept: (items: SuggestedActionItem[]) => void;
  onEdit: () => void;
  onReject: () => void;
  onDefer: () => void;
}) {
  const SourceIcon = SOURCE_ICON[entry.capture.source] ?? Keyboard;
  const items = entry.triage?.suggested_action_items ?? [];

  return (
    <article
      onClick={onActivate}
      data-active={active}
      style={{
        padding: "var(--space-4)",
        background: active ? "var(--chip-hi, var(--chip))" : "var(--bg-2)",
        border: `1px solid ${active ? "var(--accent, var(--fg-2))" : "var(--hair)"}`,
        borderRadius: "var(--radius-card)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-3)",
        cursor: "pointer",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--space-2)",
          fontSize: 12,
          color: "var(--fg-2)",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <SourceIcon size={14} />
          {labelFor(entry.capture.source)} - {timeAgo(entry.capture.captured_at)}
        </span>
        {entry.triage?.type && entry.triage.type.length > 0 && (
          <span style={{ display: "inline-flex", gap: 4 }}>
            {entry.triage.type.map((t) => (
              <span
                key={t}
                style={{
                  padding: "2px 6px",
                  background: "var(--chip)",
                  borderRadius: 4,
                  fontSize: 11,
                }}
              >
                {t}
              </span>
            ))}
          </span>
        )}
      </header>

      <p
        style={{
          margin: 0,
          color: "var(--fg)",
          fontSize: 14,
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
        }}
      >
        {entry.capture.content}
      </p>

      {entry.triage?.health_implications && (
        <p
          style={{
            margin: 0,
            padding: "var(--space-2) var(--space-3)",
            background: "var(--chip)",
            borderRadius: "var(--radius-card)",
            color: "var(--fg)",
            fontSize: 13,
          }}
        >
          Health note: {entry.triage.health_implications}
        </p>
      )}

      {items.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {items.map((it, i) => (
            <li
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 10px",
                background: "var(--bg)",
                border: "1px solid var(--hair)",
                borderRadius: "var(--radius-card)",
                fontSize: 13,
              }}
            >
              <span style={{ flex: 1, color: "var(--fg)" }}>{it.title}</span>
              <span style={{ color: "var(--fg-2)", fontSize: 12 }}>
                {it.category}
              </span>
              <span style={{ color: "var(--fg-2)", fontSize: 12 }}>
                ~{it.effort_minutes}m
              </span>
              {it.suggested_deadline && (
                <span style={{ color: "var(--fg-2)", fontSize: 12 }}>
                  by {formatDeadline(it.suggested_deadline)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {!entry.triage && (
        <p style={{ margin: 0, color: "var(--fg-2)", fontSize: 12 }}>
          State: triage pending.
          <br />
          Meaning: the cron picks up this capture within ~2 minutes.
          <br />
          Next move: come back shortly or wait for the page to refresh.
        </p>
      )}

      <footer
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          marginTop: "var(--space-1)",
        }}
      >
        <ActionButton
          onClick={(e) => {
            e.stopPropagation();
            onAccept(items);
          }}
          icon={Check}
          label="Accept"
          shortcut="A"
          disabled={items.length === 0}
        />
        <ActionButton
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          icon={Edit3}
          label="Edit"
          shortcut="E"
        />
        <ActionButton
          onClick={(e) => {
            e.stopPropagation();
            onReject();
          }}
          icon={X}
          label="Reject"
          shortcut="R"
        />
        <ActionButton
          onClick={(e) => {
            e.stopPropagation();
            onDefer();
          }}
          icon={Clock}
          label="Defer"
          shortcut="D"
        />
      </footer>
    </article>
  );
}

function ActionButton({
  onClick,
  icon: Icon,
  label,
  shortcut,
  disabled,
}: {
  onClick: (e: React.MouseEvent) => void;
  icon: typeof Check;
  label: string;
  shortcut: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "8px 12px",
        background: "var(--chip)",
        color: disabled ? "var(--fg-2)" : "var(--fg)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        fontSize: 13,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Icon size={14} /> {label}
      <span style={{ color: "var(--fg-2)", fontSize: 11 }}>({shortcut})</span>
    </button>
  );
}

function labelFor(source: string): string {
  switch (source) {
    case "voice":
      return "Voice";
    case "photo":
      return "Photo";
    case "keyboard":
      return "Keyboard";
    case "share_sheet":
      return "Share";
    case "browser_ext":
      return "Browser";
    case "email":
      return "Email";
    default:
      return source;
  }
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const min = Math.round(ms / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.round(hr / 24);
  return `${d}d ago`;
}

function formatDeadline(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });
}
